import * as React from 'react';
import {
    Dialog,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogContent,
    Button,
    Divider,
    Skeleton,
    SkeletonItem,
    Text,
    Caption1,
    Subtitle2Stronger,
    makeStyles,
    tokens,
} from '@fluentui/react-components';
import { AnalyticsRawResult } from '../../Service/ServerAPI';

interface AnalyticsResultsDialogProps {
    result: AnalyticsRawResult | null;
    isScriptRunning: boolean;
    showDialog: boolean;
    onClose: () => void;
}

const useStyles = makeStyles({
    dialogContent: {
        padding: '20px',
    },
    dialogFooter: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '10px',
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
    },
    executionTime: {
        color: tokens.colorNeutralForeground3,
    },
    divider: {
        marginTop: tokens.spacingVerticalM,
        marginBottom: tokens.spacingVerticalM,
    },
    scrollArea: {
        marginTop: tokens.spacingVerticalS,
        maxHeight: '50vh',
        overflowY: 'auto',
        // Keep the scrollbar from crowding the value column.
        paddingRight: tokens.spacingHorizontalM,
    },
    row: {
        display: 'flex',
        alignItems: 'baseline',
        gap: tokens.spacingHorizontalL,
        paddingTop: tokens.spacingVerticalSNudge,
        paddingBottom: tokens.spacingVerticalSNudge,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    headerRow: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: tokens.colorNeutralBackground1,
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    label: {
        flexGrow: 1,
        minWidth: 0,
        wordBreak: 'break-word',
        color: tokens.colorNeutralForeground1,
    },
    value: {
        flexShrink: 0,
        textAlign: 'right',
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
        color: tokens.colorNeutralForeground1,
    },
    headerCell: {
        color: tokens.colorNeutralForeground2,
        fontWeight: tokens.fontWeightSemibold,
    },
    emptyText: {
        marginTop: tokens.spacingVerticalS,
        color: tokens.colorNeutralForeground3,
    },
    skeletonRow: {
        paddingBottom: tokens.spacingVerticalS,
    },
});

function formatValue(value: number | string): string {
    return typeof value === 'number' ? value.toLocaleString() : String(value);
}

const AnalyticsResultsDialog: React.FC<AnalyticsResultsDialogProps> = ({
    result,
    isScriptRunning,
    showDialog,
    onClose,
}) => {
    const classes = useStyles();

    const renderBody = () => {
        if (isScriptRunning) {
            return (
                <Skeleton>
                    <div className={classes.body}>
                        <div className={classes.skeletonRow}>
                            <SkeletonItem size={16} />
                        </div>
                        <div className={classes.skeletonRow}>
                            <SkeletonItem size={16} />
                        </div>
                        <div className={classes.skeletonRow}>
                            <SkeletonItem size={16} />
                        </div>
                    </div>
                </Skeleton>
            );
        }

        if (!result) {
            return <Text className={classes.emptyText}>Something bad happened.</Text>;
        }

        if (result.error) {
            return <Text className={classes.emptyText}>{result.error}</Text>;
        }

        const entries = Object.entries(result.results ?? {});

        return (
            <div className={classes.body}>
                {result.executeTimeMs != null && result.executeTimeMs > 0 && (
                    <>
                        <Caption1 className={classes.executionTime}>
                            Execution Time: {Math.floor(result.executeTimeMs / 1000)} seconds
                        </Caption1>
                        <Divider className={classes.divider} />
                    </>
                )}
                <Subtitle2Stronger>Emitted Results</Subtitle2Stronger>
                {entries.length === 0 ? (
                    <Text className={classes.emptyText}>
                        The script did not emit any results.
                    </Text>
                ) : (
                    <div className={classes.scrollArea}>
                        <div className={`${classes.row} ${classes.headerRow}`}>
                            <Caption1 className={`${classes.label} ${classes.headerCell}`}>
                                Label
                            </Caption1>
                            <Caption1 className={`${classes.value} ${classes.headerCell}`}>
                                Value
                            </Caption1>
                        </div>
                        {entries.map(([label, value]) => (
                            <div key={label} className={classes.row}>
                                <Text className={classes.label}>{label}</Text>
                                <Text className={classes.value}>{formatValue(value)}</Text>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <Dialog open={showDialog} onOpenChange={() => onClose()}>
            <DialogSurface>
                <DialogTitle>Results</DialogTitle>
                <DialogBody>
                    <DialogContent className={classes.dialogContent}>
                        {renderBody()}
                    </DialogContent>
                    <div className={classes.dialogFooter}>
                        <Button onClick={onClose}>Close</Button>
                    </div>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default AnalyticsResultsDialog;
